import React, {type FC, useEffect, useRef, useState} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/CloseRounded';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import MuiSelect from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';

import {FlexColumn} from 'src/components/flex';
import TextField from 'src/components/input/text-field/TextField';
import Select from 'src/components/input/select/Select';
import {InboxFilterSelectStatus, InboxFiltersSelect} from 'src/pages/landlord/inboxes/InboxFilterSelect';
import {INITIAL_FILTERS} from 'src/pages/landlord/inboxes/constants';
import type {TConversationsFilter} from 'src/services/api/messages/use-conversations-search';


export type SubmitPayload = {
    name: string;
    is_private: boolean;
    filters: TConversationsFilter;
};

type Props = {
    handleClose: () => void;
    onSubmit: (v: SubmitPayload, id?: number) => void;
    initialValues?: {id?: number, name?: string, description?: string, is_private?: boolean, filters?: TConversationsFilter};
    isLoading?: boolean;
    errorMsg?: string;
}

export const InboxCreateEditForm: FC<Props> = ({ handleClose, onSubmit, initialValues, isLoading, errorMsg = '' }) => {

    const filtersSelectRef = useRef<HTMLDivElement>(null);
    const [isFiltersSelectOpen, setIsFiltersSelectOpen] = useState(false);
    const form = useForm({
        defaultValues: {
            name: '',
            filters: cloneDeep(INITIAL_FILTERS),
            is_private: 'false',
        },
    });

    useEffect(() => {
        if (initialValues) {
            form.reset({
                name: initialValues.name || '',
                filters: initialValues.filters || cloneDeep(INITIAL_FILTERS),
                is_private: initialValues.is_private ? 'true' : 'false',
            });
        } else {
            form.reset({
                name: '',
                filters: cloneDeep(INITIAL_FILTERS),
                is_private: 'false',
            });
        }
    }, [initialValues]);

    const name = form.watch('name');
    const filters = form.watch('filters');
    const isPrivate = form.watch('is_private');

    const submit = form.handleSubmit(() => {
        const payload = {
            name,
            is_private: isPrivate === 'true',
            filters,
        };

        onSubmit(payload);
    });


    const resetFilters = () => {
        form.setValue('filters', cloneDeep(INITIAL_FILTERS), { shouldValidate: true });
    };

    const setFilters = (newFilters: Partial<TConversationsFilter>) => {
        form.setValue('filters', {
            ...filters,
            ...newFilters,
        }, { shouldValidate: true });
    };

    const isEdit = !!initialValues?.id;

    const title = isEdit ? 'Edit inbox' : 'Create new inbox';
    const saveButtonLabel = isEdit ? 'Save changes' : 'Create Inbox';

    return <FormProvider {...form}>
        <Dialog
            open={true}
            onClose={handleClose}
            fullWidth
            sx={{
                '& .MuiChip-root': { maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' },
                '& .MuiSvgIcon-root': { ml: 1, fontSize: 20 }
            }}
        >
            <DialogTitle id="create-inbox-title">
                {title}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                <FlexColumn rowGap={2} gap={3}>
                    <Controller
                        name="name"
                        control={form.control}
                        rules={{ required: 'This field is required' }}
                        render={({ field: { value, onChange, onBlur, ...rest } , fieldState }) => (
                            <TextField
                                {...rest}
                                type="text"
                                label="Name"
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                slotProps={{
                                    htmlInput: {
                                        maxLength: 15
                                    },
                                    inputLabel: { shrink: true }
                                }}
                                size="small"
                                fullWidth
                                error={fieldState.isDirty && fieldState.invalid}
                                helperText={form.formState.errors.name?.message}
                            />
                        )}
                    />
                    <Controller
                        name="is_private"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <Select
                                {...field}
                                label="Who can see this?"
                                options={[{value: 'true', label: 'Only me'}, {value: 'false', label: 'Whole Team'}]}
                                value={field.value}
                                onChange={(value) => field.onChange(value)}
                                error={fieldState.isDirty && fieldState.invalid}
                                helperText={form.formState.errors.is_private?.message}
                                size="small"
                                fullWidth
                                data-testid="is_private-select"
                            />
                        )}
                    />
                    <Controller
                        name="filters"
                        control={form.control}
                        rules={{
                            validate: {
                                required: (value) => {
                                    if (isEqual(value, INITIAL_FILTERS)) {
                                        return 'Please set at least one filter';
                                    }
                                    return true;
                                }
                            }
                        }}
                        render={({ field: { value }, fieldState: { invalid } }) => {
                            return (
                                <FlexColumn>
                                    <MuiSelect displayEmpty id="filters" size="small" renderValue={() => {
                                        return <InboxFilterSelectStatus filter={value} setFilter={setFilters} />;
                                    }}
                                    error={invalid}
                                    ref={filtersSelectRef}
                                    onClick={() => setIsFiltersSelectOpen(true)}
                                    open={isFiltersSelectOpen}
                                    />

                                    <InboxFiltersSelect
                                        setFilter={setFilters}
                                        resetFilter={resetFilters}
                                        filter={value}
                                        anchorEl={filtersSelectRef.current}
                                        isFilterOpen={isFiltersSelectOpen}
                                        closeFilter={() => setIsFiltersSelectOpen(false)}
                                    />
                                    {!!form.formState.errors.filters?.message && <FormHelperText error>{form.formState.errors.filters?.message}</FormHelperText>}
                                </FlexColumn>
                            );
                        }}
                    />
                </FlexColumn>
                {errorMsg && <FormHelperText>{errorMsg}</FormHelperText>}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={submit} loading={isLoading} disabled={!form.formState.isValid}>{saveButtonLabel}</Button>
            </DialogActions>
        </Dialog>
    </FormProvider>;
};
